import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Calendar,
  Clock,
  Users,
  MapPin,
  Phone,
  Mail,
  CheckCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { CalendarIcon } from "lucide-react";
import { format, addDays, isToday, isTomorrow } from "date-fns";
import { cn } from "@/lib/currencyFormatter";
import {
  extractStoreIdFromSlug,
  fetchReservationSettingsBySlug,
} from "@/services/reservations/reservationService";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { useStoreReservationStore } from "@/store/storeReservationStore";
import { useStoreManagementStore } from "@/store/storeManagement";
import { CalendarIntegration } from "@/components/reservations/CalendarIntegration";

// Add this type for reservation data
type Reservation = {
  reservation_time: string;
  reservation_date: string;
  party_size: number;
};

// Add this new component for time slot cells with availability check
const TimeSlotCell = ({
  time,
  selected,
  onClick,
  isAvailable,
  reservedCount = 0,
}: {
  time: string;
  selected: boolean;
  onClick: () => void;
  isAvailable: boolean;
  reservedCount?: number;
}) => {
  return (
    <button
      onClick={onClick}
      disabled={!isAvailable}
      className={cn(
        "relative p-4 rounded-xl transition-all duration-200",
        "before:absolute before:inset-0 before:rounded-xl before:transition-all before:duration-200",
        isAvailable
          ? "hover:before:bg-gradient-to-br hover:before:from-primary/10 hover:before:to-secondary/20 hover:before:opacity-100 before:opacity-0"
          : "opacity-50 cursor-not-allowed hover:before:bg-gradient-to-br hover:before:from-destructive/10 hover:before:to-destructive/20",
        selected
          ? "before:bg-gradient-to-br before:from-primary/20 before:to-secondary/30 before:opacity-100 ring-2 ring-primary/20"
          : "",
        "group"
      )}
    >
      <div className="relative z-10">
        <p className="font-medium text-sm">{time}</p>
        <p className="text-xs text-muted-foreground">
          {isAvailable ? "Available" : "Already Reserved"}
        </p>
      </div>
    </button>
  );
};

// Helper function to normalize time format
const normalizeTimeFormat = (time: string) => {
  // If time includes seconds, remove them
  if (time.split(":").length === 3) {
    return time.slice(0, 5); // Take only HH:mm part
  }
  return time;
};

export default function PublicReservationBooking() {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const storeIdFromSlug = extractStoreIdFromSlug(storeId || "");
  const { selectedStore, setSelectedStore, setSelectedStoreId } =
    useStoreManagementStore();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [storeData, setStoreData] = useState<any>(null);
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    reservation_date: new Date(),
    reservation_time: "",
    party_size: 2,
    special_requests: "",
  });
  const { createReservation } = useStoreReservationStore();

  const [reservations, setReservations] = useState<Reservation[]>([]);

  useEffect(() => {
    if (storeId) {
      loadStoreData();
    }
  }, [storeId]);

  useEffect(() => {
    if (storeData) {
      const timeSlotsElement = document.getElementById("time-slots-grid");
      timeSlotsElement?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [storeData]);

  const loadStoreData = async () => {
    if (!storeId) return;

    setLoading(true);
    try {
      const data = await fetchReservationSettingsBySlug(storeId);

      if (data) {
        setStoreData(data);
        setSelectedStore(data.store);
        setSelectedStoreId(data.store.id);
      } else {
        toast({
          title: t("reservationBooking.errors.storeNotFound.title"),
          description: t("reservationBooking.errors.storeNotFound.description"),
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error loading store data:", error);
      toast({
        title: t("reservationBooking.errors.loadingFailed.title"),
        description: t("reservationBooking.errors.loadingFailed.description"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.customer_name ||
      !formData.customer_email ||
      !formData.reservation_date ||
      !formData.reservation_time
    ) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      const newReservation = await createReservation({
        store_id: storeData.store.id,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        reservation_date: format(formData.reservation_date, "yyyy-MM-dd"),
        reservation_time: formData.reservation_time,
        party_size: formData.party_size,
        special_requests: formData.special_requests,
        status: "confirmed",
      });

      if (newReservation) {
        setSuccess(true);
        toast({
          title: t("reservationBooking.confirmationTitle"),
          description: t("reservationBooking.confirmationDescription", {
            storeName: storeData.store.name,
            email: formData.customer_email,
          }),
        });
      }
    } catch (error) {
      console.error("Error creating reservation:", error);
      toast({
        title: "Booking Failed",
        description:
          "There was an issue with your reservation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const generateTimeSlots = () => {
    if (!storeData?.store?.business_hours) {
      return [];
    }

    const selectedDate = formData.reservation_date;
    if (!selectedDate) {
      return [];
    }

    const dayName = format(selectedDate, "EEEE").toLowerCase();

    const dayHours = storeData.store.business_hours[dayName];

    if (!dayHours) {
      return [];
    }

    if (dayHours.is_closed) {
      return [];
    }

    const slots = [];
    const [openHour, openMinute] = dayHours.open.split(":").map(Number);
    const [closeHour, closeMinute] = dayHours.close.split(":").map(Number);

    // Get current time for today's slots
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    // For today, if we're past closing time, return empty array
    if (
      isToday(selectedDate) &&
      (currentHour > closeHour ||
        (currentHour === closeHour && currentMinute >= closeMinute))
    ) {
      return [];
    }

    let startHour;
    let startMinute;

    if (isToday(selectedDate)) {
      const isBeforeOpening =
        currentHour < openHour ||
        (currentHour === openHour && currentMinute < openMinute);

      if (isBeforeOpening) {
        startHour = openHour;
        startMinute = openMinute;
      } else {
        startHour = currentHour;
        startMinute = Math.ceil(currentMinute / 30) * 30;
        if (startMinute >= 60) {
          startHour += 1;
          startMinute = 0;
        }
      }
    } else {
      startHour = openHour;
      startMinute = openMinute;
    }

    // Generate slots
    for (let hour = startHour; hour <= closeHour; hour++) {
      for (
        let minute = hour === startHour ? startMinute : 0;
        minute < 60;
        minute += 30
      ) {
        // Skip if we're at closing hour and past closing minutes
        if (hour === closeHour && minute >= closeMinute) {
          continue;
        }

        // Format time as HH:mm
        const timeString = `${hour.toString().padStart(2, "0")}:${minute
          .toString()
          .padStart(2, "0")}`;
        slots.push(timeString);
      }
    }

    return slots;
  };

  const getDateLabel = (date: Date) => {
    if (isToday(date)) return "Today";
    if (isTomorrow(date)) return "Tomorrow";
    return format(date, "EEEE, MMM d");
  };

  // Modify the isTimeSlotAvailable function to handle the time format correctly
  const isTimeSlotAvailable = (time: string) => {
    if (!formData.reservation_date) return false;

    const dateStr = format(formData.reservation_date, "yyyy-MM-dd");

    const reservedCount = reservations.filter((res) => {
      const normalizedReservationTime = normalizeTimeFormat(
        res.reservation_time
      );
      const matches =
        normalizedReservationTime === time && res.reservation_date === dateStr;

      return matches;
    }).length;

    return reservedCount < 1;
  };

  // Update fetchDateReservations to include status in the logging
  const fetchDateReservations = async (date: Date) => {
    if (!storeData?.store?.id) return;

    const dateStr = format(date, "yyyy-MM-dd");

    try {
      const { data, error } = await supabase
        .from("reservations")
        .select("reservation_time, reservation_date, party_size, status")
        .eq("store_id", storeData.store.id)
        .eq("reservation_date", dateStr)
        .not("status", "eq", "cancelled");

      if (error) throw error;

      setReservations(data || []);
    } catch (error) {
      console.error("Error fetching reservations:", error);
      toast({
        title: "Error",
        description: "Failed to load availability",
        variant: "destructive",
      });
    }
  };

  // Update useEffect to fetch reservations when date changes
  useEffect(() => {
    if (formData.reservation_date) {
      fetchDateReservations(formData.reservation_date);
    }
  }, [formData.reservation_date, storeData?.store?.id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  if (!storeData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <h2 className="text-2xl font-bold mb-2">
              {t("reservationBooking.errors.storeNotFound.title")}
            </h2>
            <p className="text-muted-foreground">
              {t("reservationBooking.errors.storeNotFound.description")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-background to-green-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">
              {t("reservationBooking.confirmationTitle")}
            </h2>
            <p className="text-muted-foreground mb-4">
              {t("reservationBooking.confirmationDescription", {
                storeName: storeData.store.name,
                email: formData.customer_email,
              })}
            </p>
            <div className="bg-muted p-4 rounded-lg text-left mb-6">
              <div className="space-y-2">
                <div>
                  <strong>
                    {t("reservationBooking.reservationDetails.date")}:
                  </strong>{" "}
                  {formData.reservation_date &&
                    format(formData.reservation_date, "EEEE, MMMM do, yyyy")}
                </div>
                <div>
                  <strong>
                    {t("reservationBooking.reservationDetails.time")}:
                  </strong>{" "}
                  {formData.reservation_time}
                </div>
                <div>
                  <strong>
                    {t("reservationBooking.reservationDetails.partySize")}:
                  </strong>{" "}
                  {t("reservationBooking.reservationDetails.guests", {
                    count: formData.party_size,
                  })}
                </div>
              </div>
            </div>

            {/* Add Calendar Integration */}
            <div className="mb-6">
              <h3 className="text-sm font-medium mb-3">
                {t("reservationBooking.calendar.title")}
              </h3>
              <CalendarIntegration
                reservation={{
                  id: "temp-id",
                  store_id: storeData.store.id,
                  customer_name: formData.customer_name,
                  customer_email: formData.customer_email,
                  customer_phone: formData.customer_phone,
                  reservation_date: format(
                    formData.reservation_date,
                    "yyyy-MM-dd"
                  ),
                  reservation_time: formData.reservation_time,
                  party_size: formData.party_size,
                  special_requests: formData.special_requests,
                  status: "confirmed",
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                }}
                storeName={storeData.store.name}
              />
            </div>

            <Button
              className="w-full"
              onClick={() => {
                setSuccess(false);
                setFormData({
                  customer_name: "",
                  customer_email: "",
                  customer_phone: "",
                  reservation_date: new Date(),
                  reservation_time: "",
                  party_size: 2,
                  special_requests: "",
                });
              }}
            >
              Make Another Reservation
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const timeSlots = generateTimeSlots();

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left side: Booking form and time slots */}
            <div className="lg:col-span-8 space-y-8">
              {/* Date & Time Selection */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    {t("reservationBooking.selectDateAndTime")}
                  </CardTitle>
                  <CardDescription>
                    {t("reservationBooking.availableSlotsFor", {
                      date: getDateLabel(formData.reservation_date),
                    })}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Date Picker */}
                    <div className="flex items-center gap-4">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "justify-start text-left font-normal",
                              isToday(formData.reservation_date) &&
                                "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {isToday(formData.reservation_date) ? (
                              <span>{t("reservationBooking.selectDate")}</span>
                            ) : (
                              getDateLabel(formData.reservation_date)
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <CalendarComponent
                            mode="single"
                            selected={formData.reservation_date}
                            onSelect={(date) =>
                              setFormData((prev) => ({
                                ...prev,
                                reservation_date: date || new Date(),
                                reservation_time: "",
                              }))
                            }
                            disabled={(date) =>
                              date < new Date() ||
                              date >
                                addDays(
                                  new Date(),
                                  storeData.settings.booking_window_days
                                )
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>

                      {/* Quick date selection buttons */}
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              reservation_date: new Date(),
                              reservation_time: "",
                            }))
                          }
                          className={cn(
                            isToday(formData.reservation_date) &&
                              "bg-primary/10"
                          )}
                        >
                          {t("reservationBooking.today")}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              reservation_date: addDays(new Date(), 1),
                              reservation_time: "",
                            }))
                          }
                          className={cn(
                            isTomorrow(formData.reservation_date) &&
                              "bg-primary/10"
                          )}
                        >
                          {t("reservationBooking.tomorrow")}
                        </Button>
                      </div>
                    </div>

                    {/* Time Slots Grid */}
                    <div id="time-slots-grid" className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">
                          {t("reservationBooking.availableTimeSlots")}
                        </span>
                        {timeSlots.length > 0 && (
                          <Badge variant="outline">
                            {t("reservationBooking.slotsAvailable", {
                              count: timeSlots.filter((time) =>
                                isTimeSlotAvailable(time)
                              ).length,
                            })}
                          </Badge>
                        )}
                      </div>
                      <div className="mt-6">
                        {(() => {
                          const selectedDate = formData.reservation_date;
                          const dayName = format(
                            selectedDate,
                            "EEEE"
                          ).toLowerCase();
                          const dayHours =
                            storeData.store.business_hours[dayName];

                          // Check if store is closed for the selected day
                          if (!dayHours || dayHours.is_closed) {
                            return (
                              <div className="text-center py-8">
                                <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                <h3 className="text-lg font-medium mb-2">
                                  {t(
                                    "reservationSettings.operatingHours.noBusinessHours"
                                  )}
                                </h3>
                                <p className="text-muted-foreground">
                                  {t(
                                    "reservationSettings.operatingHours.noBusinessHoursDescription"
                                  )}
                                </p>
                              </div>
                            );
                          }

                          // Check if we're past closing time for today
                          if (isToday(selectedDate)) {
                            const now = new Date();
                            const currentHour = now.getHours();
                            const currentMinute = now.getMinutes();
                            const [closeHour, closeMinute] = dayHours.close
                              .split(":")
                              .map(Number);

                            if (
                              currentHour > closeHour ||
                              (currentHour === closeHour &&
                                currentMinute >= closeMinute)
                            ) {
                              return (
                                <div className="text-center py-8">
                                  <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                  <h3 className="text-lg font-medium mb-2">
                                    {t(
                                      "reservationSettings.operatingHours.noBusinessHours"
                                    )}
                                  </h3>
                                  <p className="text-muted-foreground">
                                    {t(
                                      "reservationSettings.operatingHours.noBusinessHoursDescription"
                                    )}
                                  </p>
                                </div>
                              );
                            }
                          }

                          // Show time slots if available
                          if (timeSlots.length > 0) {
                            return (
                              <>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                  {timeSlots.map((time) => {
                                    const [hours, minutes] = time.split(":");
                                    const formattedTime = new Date();
                                    formattedTime.setHours(parseInt(hours));
                                    formattedTime.setMinutes(parseInt(minutes));
                                    const isAvailable =
                                      isTimeSlotAvailable(time);
                                    const isSelected =
                                      formData.reservation_time === time;

                                    return (
                                      <button
                                        key={time}
                                        onClick={() =>
                                          isAvailable &&
                                          setFormData({
                                            ...formData,
                                            reservation_time: time,
                                          })
                                        }
                                        disabled={!isAvailable}
                                        className={cn(
                                          "relative p-4 rounded-xl transition-all duration-200",
                                          "border border-border/50",
                                          "hover:shadow-lg hover:shadow-primary/5",
                                          "before:absolute before:inset-0 before:rounded-xl before:transition-all before:duration-200",
                                          isAvailable
                                            ? [
                                                "hover:before:bg-gradient-to-br hover:before:from-primary/10 hover:before:to-secondary/20",
                                                "hover:border-primary/50",
                                                "hover:scale-[1.02]",
                                                "hover:before:opacity-100 before:opacity-0",
                                              ]
                                            : [
                                                "opacity-60 cursor-not-allowed",
                                                "hover:before:bg-gradient-to-br hover:before:from-destructive/5 hover:before:to-destructive/10",
                                              ],
                                          isSelected && [
                                            "before:bg-gradient-to-br before:from-primary/20 before:to-secondary/30",
                                            "before:opacity-100",
                                            "ring-2 ring-primary/20",
                                            "border-primary/50",
                                          ],
                                          "group"
                                        )}
                                      >
                                        {/* Availability indicator dot */}
                                        <div
                                          className={cn(
                                            "absolute top-2 right-2 w-2 h-2 rounded-full",
                                            isAvailable
                                              ? "bg-green-500"
                                              : "bg-destructive"
                                          )}
                                        />

                                        <div className="relative z-10">
                                          <p className="font-medium text-sm">
                                            {format(formattedTime, "h:mm a")}
                                          </p>
                                          <p className="text-xs text-muted-foreground mt-1">
                                            {isAvailable ? (
                                              <span className="text-green-600 dark:text-green-400">
                                                Available
                                              </span>
                                            ) : (
                                              <span className="text-destructive">
                                                Already Reserved
                                              </span>
                                            )}
                                          </p>
                                        </div>

                                        {/* Hover effect overlay */}
                                        {isAvailable && (
                                          <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary/5 to-secondary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              </>
                            );
                          }

                          // No time slots available
                          return (
                            <div className="text-center py-8">
                              <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                              <h3 className="text-lg font-medium mb-2">
                                {t("reservationBooking.noAvailableSlots")}
                              </h3>
                              <p className="text-muted-foreground">
                                {t("reservationBooking.tryAnotherDay")}
                              </p>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Customer Details Form */}
              <Card>
                <CardHeader>
                  <CardTitle>{t("reservationBooking.form.title")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="customer_name">
                          {t("reservationBooking.form.fullName")}{" "}
                          {t("reservationBooking.form.required")}
                        </Label>
                        <Input
                          id="customer_name"
                          value={formData.customer_name}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              customer_name: e.target.value,
                            }))
                          }
                          placeholder={t(
                            "reservationBooking.form.fullNamePlaceholder"
                          )}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="customer_email">
                          {t("reservationBooking.form.email")}
                          {t("reservationBooking.form.required")}
                        </Label>
                        <Input
                          id="customer_email"
                          type="email"
                          value={formData.customer_email}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              customer_email: e.target.value,
                            }))
                          }
                          placeholder={t(
                            "reservationBooking.form.emailPlaceholder"
                          )}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="customer_phone">
                          {t("reservationBooking.form.phone")}
                          {t("reservationBooking.form.required")}
                        </Label>
                        <Input
                          id="customer_phone"
                          type="tel"
                          value={formData.customer_phone}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              customer_phone: e.target.value,
                            }))
                          }
                          placeholder="Your phone number"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="party_size">Party Size</Label>
                        <Select
                          value={formData.party_size.toString()}
                          onValueChange={(value) =>
                            setFormData((prev) => ({
                              ...prev,
                              party_size: parseInt(value),
                            }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Number of guests" />
                          </SelectTrigger>
                          <SelectContent>
                            {[...Array(storeData.settings.max_party_size)].map(
                              (_, i) => (
                                <SelectItem
                                  key={i + 1}
                                  value={(i + 1).toString()}
                                >
                                  {i + 1} Guest{i !== 0 ? "s" : ""}
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="special_requests">
                        {t("reservationBooking.form.specialRequests")}
                      </Label>
                      <Textarea
                        id="special_requests"
                        value={formData.special_requests}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            special_requests: e.target.value,
                          }))
                        }
                        placeholder={t(
                          "reservationBooking.form.specialRequestsPlaceholder"
                        )}
                        rows={3}
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full"
                      size="lg"
                      disabled={submitting || timeSlots.length === 0}
                    >
                      {submitting ? "Booking..." : "Reserve Table"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Right side: Store info and booking details */}
            <div className="lg:col-span-4 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>
                    {t("reservationBooking.operatingHours.title")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(storeData.store.business_hours)
                      .sort(([dayA], [dayB]) => {
                        const daysOrder = {
                          monday: 1,
                          tuesday: 2,
                          wednesday: 3,
                          thursday: 4,
                          friday: 5,
                          saturday: 6,
                          sunday: 7,
                        };
                        return (
                          daysOrder[dayA as keyof typeof daysOrder] -
                          daysOrder[dayB as keyof typeof daysOrder]
                        );
                      })
                      .map(([day, hours]: [string, any]) => (
                        <div
                          key={day}
                          className="flex justify-between items-center"
                        >
                          <span className="capitalize font-medium">{day}</span>
                          {hours.is_closed ? (
                            <Badge variant="secondary">
                              {t("reservationBooking.operatingHours.closed")}
                            </Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">
                              {hours.open} - {hours.close}
                            </span>
                          )}
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>
                    {t("reservationBooking.bookingInfo.title")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium">
                        {t("reservationBooking.bookingInfo.maxPartySize.title")}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t(
                          "reservationBooking.bookingInfo.maxPartySize.description",
                          {
                            count: storeData.settings.max_party_size,
                          }
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium">
                        {t(
                          "reservationBooking.bookingInfo.bookingWindow.title"
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t(
                          "reservationBooking.bookingInfo.bookingWindow.description",
                          {
                            count: storeData.settings.booking_window_days,
                          }
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium">
                        {t(
                          "reservationBooking.bookingInfo.instantConfirmation.title"
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t(
                          "reservationBooking.bookingInfo.instantConfirmation.description"
                        )}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
